<template>
  <v-row class="d-flex flex-column">
    <!-------------------------------------------SWITCH-------------------------------------------->
    <v-col xs="12" md="12" lg="12" class="justify-center d-flex">
      <v-switch
        v-model="cameraSwitch"
        :label="$t('images.camera')"
        color="primary"
      />
    </v-col>
    <!-------------------------------------------UPLOAD-------------------------------------------->
    <div v-if="!cameraSwitch">
      <v-col xs="12" md="12" lg="12" class="justify-center d-flex">
        <v-btn color="primary" variant="outlined" @click="openInputComponent">
          <v-icon start> mdi-paperclip </v-icon>
          {{ $t("images.upload") }}
        </v-btn>

        <input
          ref="input"
          type="file"
          style="display: none"
          accept="image/*"
          multiple="multiple"
          @change="onInput"
        />
      </v-col>
    </div>
    <canvas ref="canvas" class="hideElement" />
    <!-------------------------------------------CAMERA------------------------------------->
    <div v-if="cameraSwitch">
      <v-col xs="12" md="12" lg="12" class="align-center justify-center d-flex">
        <Teleport to="body" :disabled="!isFullscreen">
          <div
            :class="[
              isFullscreen ? 'native-camera__viewfinder' : 'video-container',
              { hideElement: hideCamera && !isFullscreen },
            ]"
            :style="
              !isFullscreen
                ? {
                    maxWidth: `${videoWidth}px`,
                    aspectRatio: previewAspectRatio || undefined,
                  }
                : undefined
            "
          >
            <video
              ref="video"
              class="video-preview"
              :class="{ hideElement: hideCamera && !isFullscreen }"
              autoplay
              playsinline
              muted
            />
          </div>
        </Teleport>
        <img
          v-if="!isFullscreen"
          ref="image"
          :width="videoWidth"
          :class="{ hideElement: hideImage }"
          class="border"
        />
      </v-col>
      <v-col v-if="!isFullscreen" xs="12" md="12" lg="12" class="align-center">
        <v-row class="justify-center d-flex">
          <v-btn
            v-if="!hideCamera"
            color="#747474"
            variant="text"
            icon
            @click="switchCamera"
          >
            <v-icon>mdi-camera-switch-outline</v-icon>
          </v-btn>
          <v-btn
            :disabled="hideCamera"
            color="#05CBCD"
            variant="text"
             icon
            @click="takePicture"
          >
            <v-icon color="#747474" x-large> mdi-camera </v-icon>
          </v-btn>
          <v-btn color="#747474" variant="text" icon @click="deletePicture">
            <v-icon>mdi-delete-outline</v-icon>
          </v-btn>
          <v-btn
            color="#747474"
            variant="text"
            icon
            :title="$t('images.fullscreen')"
            @click="enterFullscreen"
          >
            <v-icon>mdi-fullscreen</v-icon>
          </v-btn>
        </v-row>
      </v-col>
    </div>
    <!-------------------------------------------NATIVE FULLSCREEN--------------------------------->
    <Teleport to="body">
      <div
        v-if="cameraSwitch && isFullscreen"
        class="native-camera"
      >
        <div class="native-camera__top">
          <button
            type="button"
            class="native-camera__icon-btn"
            :title="$t('images.exit_fullscreen')"
            @click="exitFullscreen"
          >
            <v-icon color="white" size="28">mdi-close</v-icon>
          </button>
        </div>

        <div class="native-camera__grid" />

        <div class="native-camera__bottom">
          <button
            type="button"
            class="native-camera__side-btn"
            :title="$t('images.delete')"
            @click="deletePicture"
          >
            <v-icon color="white" size="26">mdi-delete-outline</v-icon>
          </button>

          <button
            type="button"
            class="native-camera__shutter"
            :disabled="hideCamera"
            :title="$t('images.take_picture')"
            @click="takePicture"
          />

          <button
            v-if="!hideCamera"
            type="button"
            class="native-camera__side-btn"
            :title="$t('images.switch_camera')"
            @click="switchCamera"
          >
            <v-icon color="white" size="26">mdi-camera-switch-outline</v-icon>
          </button>
          <div v-else class="native-camera__side-btn native-camera__side-btn--placeholder" />
        </div>
      </div>
    </Teleport>
  </v-row>
</template>

<script>
function dataURLtoBlob(dataUrl) {
  const byteString = atob(dataUrl.split(",")[1]);
  const mimeString = dataUrl.split(",")[0].split(":")[1].split(";")[0];
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i += 1) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type: mimeString });
}

export default {
  data: () => ({
    cameraSwitch: true,
    front: false,
    hideCamera: false,
    hideImage: true,
    isFullscreen: false,
    stream: null,
    dataUrl: null,
    blob: null,
    previewAspectRatio: null,
  }),
  computed: {
    videoWidth() {
      if (typeof window !== "undefined") {
        const available = Math.floor(window.innerWidth * 0.92);
        return Math.min(Math.max(available, 320), 680);
      }
      if (this.$vuetify.display.xs) return 360;
      if (this.$vuetify.display.sm) return 480;
      return 680;
    },
  },
  watch: {
    cameraSwitch(value) {
      if (value) {
        this.startCamera();
      } else {
        this.exitFullscreen();
        this.stopCamera();
        this.dataUrl = null;
        this.blob = null;
      }
    },
    isFullscreen(value) {
      document.body.style.overflow = value ? "hidden" : "";
    },
  },
  mounted() {
    this.startCamera();
    document.addEventListener("keydown", this.onKeydown);
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.onKeydown);
    this.exitFullscreen();
    this.stopCamera();
    this.dataUrl = null;
    this.blob = null;
  },
  methods: {
    getActiveVideo() {
      return this.$refs.video;
    },
    async attachStreamToActiveVideo() {
      const videoEl = this.getActiveVideo();
      if (!videoEl || !this.stream) return;
      if (videoEl.srcObject === this.stream) {
        try {
          await videoEl.play();
        } catch (err) {
          console.error(err);
        }
        return;
      }
      videoEl.srcObject = this.stream;
      try {
        await videoEl.play();
      } catch (err) {
        console.error(err);
      }
      this.bindPreviewMetadata(videoEl);
    },
    onKeydown(event) {
      if (event.key === "Escape" && this.isFullscreen) {
        this.exitFullscreen();
      }
    },
    async enterFullscreen() {
      this.isFullscreen = true;
      await this.$nextTick();
      await this.attachStreamToActiveVideo();
    },
    async exitFullscreen() {
      if (!this.isFullscreen) return;
      this.isFullscreen = false;
      document.body.style.overflow = "";
      await this.$nextTick();
      await this.attachStreamToActiveVideo();
    },
    openInputComponent() {
      this.$refs.input.click();
    },
    onInput(input) {
      const { files } = input.target;
      for (const file of files) {
        if (file && file !== undefined) {
          const fileReader = new FileReader();
          fileReader.readAsDataURL(file);
          fileReader.addEventListener("load", () => {
            const dataUrl = fileReader.result;
            const blob = dataURLtoBlob(dataUrl);
            this.$emit("takePicture", { dataUrl, blob });
          });
        }
      }
    },
    getDefaultConstraints() {
      return {
        video: {
          facingMode: this.front ? "user" : "environment",
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      };
    },
    syncPreviewAspectRatio(videoEl) {
      if (!videoEl?.videoWidth || !videoEl.videoHeight) return;
      this.previewAspectRatio = videoEl.videoWidth / videoEl.videoHeight;
    },
    bindPreviewMetadata(videoEl) {
      if (!videoEl) return;
      const onMetadata = () => {
        this.syncPreviewAspectRatio(videoEl);
        videoEl.removeEventListener("loadedmetadata", onMetadata);
      };
      videoEl.addEventListener("loadedmetadata", onMetadata);
      if (videoEl.readyState >= 1) {
        this.syncPreviewAspectRatio(videoEl);
      }
    },
    async startCamera(constraints = null) {
      this.hideCamera = false;
      await this.$nextTick();

      const videoEl = this.getActiveVideo();
      if (!videoEl) return;

      this.stopStream();

      try {
        if (!window.isSecureContext) {
          console.error(
            "Camera requires HTTPS. On mobile use: npm run serve:local:mobile",
          );
          return;
        }
        const stream = await navigator.mediaDevices.getUserMedia(
          constraints || this.getDefaultConstraints(),
        );
        this.stream = stream;
        videoEl.srcObject = stream;
        await videoEl.play();
        this.bindPreviewMetadata(videoEl);
      } catch (err) {
        console.error(err);
      }
    },
    stopStream() {
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
        this.stream = null;
      }
      [this.$refs.video].forEach((videoEl) => {
        if (videoEl) videoEl.srcObject = null;
      });
    },
    stopCamera() {
      this.stopStream();
      this.hideCamera = true;
    },
    switchCamera() {
      this.front = !this.front;
      this.stopStream();
      this.startCamera(this.getDefaultConstraints());
    },
    async takePicture() {
      const videoEl = this.getActiveVideo();
      if (!videoEl || !videoEl.videoWidth) return;

      const { canvas } = this.$refs;
      if (!canvas) return;

      this.hideCamera = true;
      canvas.width = videoEl.videoWidth;
      canvas.height = videoEl.videoHeight;
      const context = canvas.getContext("2d");
      context.drawImage(
        videoEl,
        0,
        0,
        videoEl.videoWidth,
        videoEl.videoHeight,
      );
      const data = canvas.toDataURL("image/png");
      const blob = dataURLtoBlob(data);
      const { image } = this.$refs;
      if (image) image.setAttribute("src", data);
      this.hideImage = false;
      this.dataUrl = data;
      this.blob = blob;
      this.hideImage = true;
      this.hideCamera = false;
      this.$emit("takePicture", { dataUrl: this.dataUrl, blob: this.blob });
      this.dataUrl = null;
      this.blob = null;

      if (this.isFullscreen) {
        await this.exitFullscreen();
      }
    },
    deletePicture() {
      this.hideImage = true;
      this.hideCamera = false;
      this.dataUrl = null;
      this.blob = null;
    },
  },
};
</script>

<style scoped>
.hideElement {
  display: none !important;
}
.video-container {
  width: 100%;
  background-color: #fff;
  overflow: hidden;
}
.native-camera__viewfinder {
  position: fixed;
  inset: 0;
  z-index: 2498;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  pointer-events: none;
}
.native-camera__viewfinder .video-preview {
  width: 100%;
  height: 100%;
  max-width: 100vw;
  max-height: 100vh;
  aspect-ratio: unset;
  object-fit: contain;
}
.video-preview {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
}
.border {
  display: block;
}

/* Controls overlay — above teleported video (2498) */
.native-camera {
  position: fixed;
  inset: 0;
  z-index: 2500;
  pointer-events: none;
  overflow: hidden;
  touch-action: manipulation;
  user-select: none;
  background: transparent;
}
.native-camera__grid {
  position: fixed;
  inset: 0;
  z-index: 2499;
  pointer-events: none;
  background-image:
    linear-gradient(to right, rgba(255, 255, 255, 0.3) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(255, 255, 255, 0.3) 1px, transparent 1px);
  background-size: 33.333% 33.333%;
}
.native-camera__top {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2501;
  pointer-events: auto;
  display: flex;
  justify-content: flex-end;
  padding: 12px 16px;
  padding-top: max(12px, env(safe-area-inset-top));
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.45), transparent);
}
.native-camera__bottom {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 2501;
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 28px 40px;
  padding-bottom: max(28px, env(safe-area-inset-bottom));
  background: linear-gradient(to top, rgba(0, 0, 0, 0.55), transparent);
}
.native-camera__icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.35);
  cursor: pointer;
}
.native-camera__side-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  cursor: pointer;
  flex-shrink: 0;
}
.native-camera__side-btn--placeholder {
  visibility: hidden;
  pointer-events: none;
}
.native-camera__shutter {
  width: 76px;
  height: 76px;
  border: 5px solid #fff;
  border-radius: 50%;
  background: #fff;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
  transition: transform 0.1s ease;
}
.native-camera__shutter:active:not(:disabled) {
  transform: scale(0.9);
  background: #ddd;
}
.native-camera__shutter:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
</style>
